<script setup lang="ts">
/**
 * Last step of the guided flow: what happens next, or what is still to do.
 *   under review  thanks, and the review timeline
 *   verified      done
 *   outstanding   the documents still needed, each a click away
 *   ready         everything required is in but it has not gone for review
 *                 on its own (an optional document was not accepted and they
 *                 chose to leave it) — Submit for review
 */
import { computed, ref } from "vue";
import axios from "axios";
import { ElMessage } from "element-plus";
import {
  useCompanyKyc,
  kycUrl,
  authHeaders,
  reasonFrom,
  canUploadType,
  docsPendingVerification,
  type KycView,
} from "../../composables/useCompanyKyc";
import { inSentence } from "./docGuide";

const props = defineProps<{ view: KycView; plan: string[] }>();
const emit = defineEmits<{ (e: "go", key: string): void; (e: "exit"): void }>();
const kyc = useCompanyKyc();

const status = computed(() => props.view.company.kyc_status);
const pendingVerification = computed(() => docsPendingVerification(props.view));
const company = computed(() => props.view.company.company_name);
const gateOn = computed(() => !!props.view.order_gate?.enabled);
const live = (t: KycView["document_types"][number]) =>
  !!t.document && t.document.review_status !== "rejected" && !t.document.expired;
const outstanding = computed(() =>
  props.view.document_types.filter((t) => t.is_required && !live(t)),
);
const uploadable = computed(() => outstanding.value.filter((t) => canUploadType(props.view, t)));
// What "Almost there" lists: under review, only what can be uploaded now (a
// document staff rejected mid-review waits for their decision).
const toDo = computed(() => (status.value === "under_review" ? uploadable.value : outstanding.value));
const optionalRejected = computed(() =>
  props.view.document_types.filter((t) => !t.is_required && t.document?.review_status === "rejected"),
);

type State = "review" | "verified" | "nothing" | "outstanding" | "ready";
const state = computed<State>(() => {
  // Under review but missing a type made required since: it can be uploaded,
  // and the review can't finish without it.
  if (status.value === "under_review") return uploadable.value.length ? "outstanding" : "review";
  if (status.value === "approved") return "verified";
  if (!["pending", "rejected"].includes(status.value || "")) return "nothing";
  return outstanding.value.length ? "outstanding" : "ready";
});

const fmtDay = (d: string | null) =>
  d ? new Date(d).toLocaleDateString("en-KE", { day: "numeric", month: "long", year: "numeric" }) : "";

function open(code: string) {
  if (props.plan.includes(code)) emit("go", code);
  else window.location.assign(`/verify?doc=${encodeURIComponent(code)}`);
}

const submitting = ref(false);
async function submit() {
  submitting.value = true;
  try {
    const res = await axios.post(kycUrl("/submit"), {}, { headers: authHeaders() });
    kyc.adopt(res.data?.data || null);
  } catch (err) {
    ElMessage({ message: reasonFrom(err, "Could not send them for review. Please try again."), type: "error" });
    void kyc.load(true);
  } finally {
    submitting.value = false;
  }
}
</script>

<template>
  <section class="fin vf-card">
    <!-- Under review -->
    <template v-if="state === 'review'">
      <span class="fin-badge is-ok" aria-hidden="true">
        <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
      </span>
      <h1 class="fin-title" tabindex="-1">Thanks — we have your documents</h1>
      <p class="fin-lede">
        We're reviewing them now and will email your admins as soon as we're done.
        {{ gateOn ? "You can place orders once they're approved." : "You can keep placing orders meanwhile." }}
      </p>
      <ol class="fin-timeline">
        <li class="is-done">
          <span class="fin-timeline__dot"></span>
          <div><strong>Documents sent</strong><span>{{ fmtDay(view.company.kyc_submitted_at) }}</span></div>
        </li>
        <li class="is-current">
          <span class="fin-timeline__dot"></span>
          <div><strong>Xobo reviews them</strong><span>We check each one against your registration details.</span></div>
        </li>
        <li>
          <span class="fin-timeline__dot"></span>
          <div><strong>{{ company }} is verified</strong><span>If anything needs replacing, we'll email you and say exactly what.</span></div>
        </li>
      </ol>
      <div class="fin-actions">
        <button type="button" class="vf-btn" @click="emit('exit')">{{ gateOn ? "Back to Xobo" : "Place an order" }}</button>
        <router-link to="/account/verification" class="vf-btn vf-btn--ghost">See verification status</router-link>
      </div>
    </template>

    <!-- Verified -->
    <template v-else-if="state === 'verified'">
      <span class="fin-badge is-ok" aria-hidden="true">
        <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><polyline points="9 12 11 14 15 10" /></svg>
      </span>
      <h1 class="fin-title" tabindex="-1">{{ company }} is verified</h1>
      <p v-if="pendingVerification.length" class="fin-lede">
        Your {{ pendingVerification.map((t) => inSentence(t.label)).join(" and ") }}
        {{ pendingVerification.length > 1 ? "are" : "is" }} pending verification: Xobo will check
        {{ pendingVerification.length > 1 ? "them" : "it" }} and you'll see the result on your Verification page.
      </p>
      <p v-else class="fin-lede">There's nothing more to send. If your registration details change, let us know and we'll ask for updated documents.</p>
      <div class="fin-actions">
        <button type="button" class="vf-btn" @click="emit('exit')">Place an order</button>
      </div>
    </template>

    <!-- Still to do -->
    <template v-else-if="state === 'outstanding'">
      <span class="fin-badge is-warn" aria-hidden="true">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
      </span>
      <h1 class="fin-title" tabindex="-1">Almost there</h1>
      <p class="fin-lede">
        {{ toDo.length === 1 ? "One more document" : `${toDo.length} more documents` }} and we can
        {{ status === "under_review" ? "finish" : "start" }} the review.
        Your progress is saved — you can come back to this any time from <strong>Account → Verification</strong>.
      </p>
      <ul class="fin-list">
        <li v-for="t in toDo" :key="t.doc_type">
          <span>{{ t.label }}</span>
          <button
            v-if="canUploadType(view, t)"
            type="button"
            class="vf-btn vf-btn--ghost fin-list__btn"
            @click="open(t.doc_type)"
          >{{ t.document ? "Replace" : "Upload" }}</button>
        </li>
      </ul>
      <div class="fin-actions">
        <button v-if="uploadable.length" type="button" class="vf-btn" @click="open(uploadable[0].doc_type)">Continue</button>
        <button type="button" class="vf-btn vf-btn--link" @click="emit('exit')">I'll finish later</button>
      </div>
    </template>

    <!-- Everything required is in, but it hasn't gone for review -->
    <template v-else-if="state === 'ready'">
      <span class="fin-badge is-info" aria-hidden="true">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>
      </span>
      <h1 class="fin-title" tabindex="-1">Ready to send</h1>
      <p class="fin-lede">
        Every required document is in.
        <template v-if="optionalRejected.length">
          We couldn't accept your {{ optionalRejected.map((t) => inSentence(t.label)).join(" or ") }},
          but {{ optionalRejected.length > 1 ? "they're" : "it's" }} optional — you can send without {{ optionalRejected.length > 1 ? "them" : "it" }}.
        </template>
      </p>
      <div class="fin-actions">
        <button type="button" class="vf-btn" :disabled="submitting" @click="submit">{{ submitting ? "Sending…" : "Send for review" }}</button>
        <button v-if="optionalRejected.length" type="button" class="vf-btn vf-btn--ghost" @click="open(optionalRejected[0].doc_type)">Replace it first</button>
      </div>
    </template>

    <!-- Nothing to do -->
    <template v-else>
      <span class="fin-badge is-info" aria-hidden="true">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="8 12 11 15 16 9" /></svg>
      </span>
      <h1 class="fin-title" tabindex="-1">Nothing to send</h1>
      <p class="fin-lede">Your account doesn't need company documents right now. If we ever need them, we'll email your admins.</p>
      <div class="fin-actions">
        <button type="button" class="vf-btn" @click="emit('exit')">Back to Xobo</button>
      </div>
    </template>
  </section>
</template>

<style scoped>
.fin { padding: 44px 44px 36px; display: flex; flex-direction: column; align-items: flex-start; gap: 14px; }
.fin-badge {
  width: 64px;
  height: 64px;
  border-radius: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.fin-badge.is-ok { background: var(--vf-ok); color: #fff; box-shadow: 0 10px 24px -10px rgba(21, 128, 61, 0.6); }
.fin-badge.is-warn { background: var(--vf-warn-bg); color: var(--vf-warn); }
.fin-badge.is-info { background: var(--vf-info-bg); color: var(--vf-navy); }
.fin-title { margin: 8px 0 0; font-size: 1.8rem; line-height: 1.2; font-weight: 700; color: var(--vf-navy); letter-spacing: -0.01em; }
.fin-lede { margin: 0; max-width: 60ch; font-size: 1rem; line-height: 1.6; color: var(--vf-text); }

.fin-timeline { list-style: none; margin: 14px 0 6px; padding: 0; display: flex; flex-direction: column; }
.fin-timeline li { position: relative; display: flex; gap: 14px; padding-bottom: 20px; }
.fin-timeline li:last-child { padding-bottom: 0; }
.fin-timeline li::before {
  content: "";
  position: absolute;
  left: 8px;
  top: 20px;
  bottom: 0;
  width: 2px;
  background: var(--vf-line);
}
.fin-timeline li:last-child::before { display: none; }
.fin-timeline__dot {
  position: relative;
  z-index: 1;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  margin-top: 2px;
  border-radius: 50%;
  border: 2px solid #c6d0de;
  background: #fff;
}
.fin-timeline li.is-done .fin-timeline__dot { border-color: var(--vf-ok); background: var(--vf-ok); }
.fin-timeline li.is-done::before { background: var(--vf-ok); }
.fin-timeline li.is-current .fin-timeline__dot { border-color: var(--vf-navy); box-shadow: 0 0 0 4px rgba(18, 40, 74, 0.12); }
.fin-timeline div { display: flex; flex-direction: column; gap: 2px; }
.fin-timeline strong { font-size: 0.95rem; color: var(--vf-ink); }
.fin-timeline span { font-size: 0.86rem; color: var(--vf-text); }

.fin-list { list-style: none; margin: 6px 0 0; padding: 0; width: 100%; display: flex; flex-direction: column; gap: 8px; }
.fin-list li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid var(--vf-line);
  border-radius: 12px;
  font-size: 0.92rem;
  font-weight: 600;
}
.fin-list__btn { height: 36px; padding: 0 16px; font-size: 0.84rem; }

.fin-actions { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 10px; }

@media (max-width: 640px) {
  .fin { padding: 28px 20px 24px; }
  .fin-title { font-size: 1.45rem; }
  .fin-actions { width: 100%; flex-direction: column; }
  .fin-actions .vf-btn { width: 100%; }
}
</style>
